/**
 * HubSpotAIEditor
 *
 * Workflow node editor for the "HubSpot AI" action.
 *
 * Architecture:
 *   ① AI Config      — which LLM + system prompt
 *   ② HubSpot Config — which HubSpot portal (credentials)
 *   ③ MCP Servers    — tools the AI can call (hubspot_search_contact, hubspot_push_note, etc.)
 *   ④ Scenario Prompts — copy/paste snippets for the AI Config system prompt
 *   ⑤ Specialists    — optional delegation to sub-topic AI agents
 *
 * HubSpot live tools (admin controller):
 *   - hubspot_search_contact  → GET /HubSpot/Contacts/Search (by email/name/phone)
 *   - hubspot_push_note       → POST /HubSpot/Notes (attach note/transcript to contact)
 *
 * Transcript auto-push (PushTranscripts flag):
 *   When enabled on HubSpotConfig, the messaging service automatically pushes the full
 *   chat transcript to the contact's timeline when the conversation is resolved.
 *   This is SEPARATE from the AI — it happens regardless of which workflow node ran.
 *   The AI node lets the AI decide to push notes mid-conversation (e.g. qualified lead summary).
 */
import * as React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useHubSpotConfigs from "@/hooks/useHubSpotConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

/**
 * Scenario prompts — copy into the AI Config's system prompt.
 * Written as behavioral guidance, not rigid steps, so the AI adapts to context.
 * The AI won't re-ask for info the visitor already provided.
 */
const HUBSPOT_SCENARIOS = [
  {
    id: "contact_identify",
    label: "Identify & Look Up Visitor",
    description: "Identify who the visitor is against HubSpot contacts before answering their question.",
    prompt: `When a visitor starts a conversation, ask for their email address early if you don't already have it. Use hubspot_search_contact to find their HubSpot contact record. If found, greet them by name and reference their company if available. If not found, proceed normally and offer to create a record at the end. Never block the conversation waiting for identity — if they won't share their email, continue helping them anyway.`
  },
  {
    id: "lead_capture",
    label: "Qualify & Capture as Lead",
    description: "Collect lead details conversationally and log them to HubSpot.",
    prompt: `Your goal is to qualify this visitor as a HubSpot lead. Collect the following naturally during the conversation — not all at once: first name, last name, business email, company name, and what they're looking for (topic or pain point). Once collected, use hubspot_push_note to attach a lead qualification summary to their contact (or create a note even if no contact exists, including all collected details). Format the note: "Lead Qualification — [date]\nName: ...\nCompany: ...\nTopic: ...\nKey points: ...". Confirm to the visitor that your team will follow up.`
  },
  {
    id: "push_transcript",
    label: "Push Conversation Summary to HubSpot",
    description: "At the end of a conversation, attach a summary note to the contact's HubSpot timeline.",
    prompt: `At the end of the conversation, look up the visitor using hubspot_search_contact with their email address (ask if you don't have it). If found, use hubspot_push_note to attach a concise conversation summary (150 words max) to their contact record. Include: main topic, outcome, and any follow-up items. Note title: "Velaro Chat — [today's date]". If the contact isn't found, still log the note with a flag: "[No HubSpot contact found — email: visitor@example.com]" so the team can follow up. This note supplements the auto-transcript push — it's a human-readable summary, not the raw transcript.`
  },
  {
    id: "support_intake",
    label: "Support Intake — Log Issue to HubSpot",
    description: "Visitor has a support issue — identify them, understand the problem, log it.",
    prompt: `When the visitor describes a problem, first identify them using hubspot_search_contact by email. Ask for their issue description and urgency (low / medium / high). Use hubspot_push_note to log the support request on their contact record with format: "Support Request — [date]\nIssue: [description]\nUrgency: [level]\nChannel: Live Chat". Confirm the ticket is logged and provide a case reference if available. If the issue is critical or you can't help, escalate to a human agent.`
  },
  {
    id: "sales_followup",
    label: "Sales Follow-up / Returning Visitor",
    description: "Returning prospect — look up their HubSpot history, continue the conversation from where it left off.",
    prompt: `When a returning visitor starts chatting, ask for their email to look them up using hubspot_search_contact. If you find their record, acknowledge any prior engagement (e.g. "I see you spoke with us about X last month"). Pick up the conversation from their last known need. At the end, push an update note to their record summarizing today's interaction. This creates a continuous relationship record in HubSpot without the visitor needing to re-explain their situation every time.`
  }
];

export default function HubSpotAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: hubSpotConfigs, loading: hsLoading } = useHubSpotConfigs();
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const hsOptions = hubSpotConfigs
    .filter((h) => h.enabled)
    .map((h) => ({
      value: h.id,
      label: h.displayName || h.portalName || `Config #${h.id}`
    }));
  const allHsOptions = hubSpotConfigs.map((h) => ({
    value: h.id,
    label: (h.displayName || h.portalName || `Config #${h.id}`) + (h.enabled ? "" : " (disabled)")
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* graceful fallback */ }
  }

  // Show transcript push status if any config has it enabled
  const transcriptPushEnabled = hubSpotConfigs.some((h) => h.enabled && h.pushTranscripts);

  return (
    <>
      <AISetupGuide
        dataSourceName="HubSpot"
        dataSourcePath="/integrations/hubspot"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={!!node.hubSpotConfigId}
      />

      {/* Transcript auto-push notice */}
      {transcriptPushEnabled && (
        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 flex items-start gap-2">
          <svg className="w-3.5 h-3.5 mt-0.5 shrink-0" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.2 5.5l-4 4a.7.7 0 01-1 0l-2-2a.7.7 0 011-1l1.5 1.5 3.5-3.5a.7.7 0 011 1z" />
          </svg>
          <span>
            <strong>Auto-transcript push is ON</strong> — full chat transcripts are automatically
            attached to HubSpot contacts when conversations end. The AI node adds intelligent
            summaries and mid-conversation notes on top of this.
          </span>
        </div>
      )}

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <Link to="/bots" className="underline font-medium">Create an AI Config first →</Link>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt — use the Scenario Prompts below to tell it
              how to handle HubSpot conversations.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            No system prompt yet.{" "}
            <Link to="/bots" className="underline">Add one →</Link> Use the Scenario Prompts below to get started.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: HubSpot Config ── */}
      <FormGroup label="HubSpot Portal">
        {!hsLoading && hubSpotConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No HubSpot portal connected.{" "}
            <Link to="/integrations/hubspot" className="underline font-medium">Connect HubSpot →</Link>
            <p className="mt-1 text-slate-500">
              The HubSpot config stores your private app token so the AI can search contacts
              and push notes to your portal.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select HubSpot Portal —" },
                ...(hsOptions.length > 0 ? hsOptions : allHsOptions)
              ]}
              value={node.hubSpotConfigId ?? 0}
              onChange={(value) => onUpdate({ hubSpotConfigId: Number(value) || undefined })}
            />
            {hubSpotConfigs.length > 0 && hsOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                All portals are disabled. <Link to="/integrations/hubspot" className="underline">Enable one →</Link>
              </p>
            )}
          </>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <Link to="/integrations/mcp" className="text-blue-600 underline">Add an MCP server →</Link>
            <p className="mt-1">
              MCP servers give the AI tools to call:{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">hubspot_search_contact</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">hubspot_push_note</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">hubspot_create_deal</code>, etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && <span className="text-xs text-slate-500">{server.description}</span>}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">+{server.toolNames.length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario Prompts</span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. They're behavioral guidelines — the AI
          adapts based on what the visitor already shared, so it won't ask for info it already has.
        </p>
        <div className="flex flex-col gap-2">
          {HUBSPOT_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied ? "border-green-300 bg-green-50 text-green-700" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">Paste into your AI Config's system prompt, then customize for your business.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AIContextInspector mcpToolNames={enabledToolNames} />

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection node={node} onUpdate={onUpdate} aiConfigurations={aiConfigurations} enabledMcpServers={enabledMcpServers} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
